import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountStore } from '../Stores/accountStore';
import { useMaterialsStore } from '../Stores/MaterialsStore';
import { GENERAL_ID, useExposureStore } from '../Stores/exposureDataStore';
import { useExposureCalculation } from '../../hooks/useExposureCalculation';
import { MaterialTrendButton, singleMaterialCardProps } from '../../pages/Dashboard/MaterialTrendButton';
import { getExposureStatus, ExposureTypes } from '../../utils/RiskUtils';
import { useCardBarStore } from './useCardBarConfig';

/**
 * Shared formula card bar — rendered once in the layout.
 * Pages configure it via useCardBarStore.setConfig() on mount.
 */
export const FormulaCardBar: React.FC = () => {
  const { config } = useCardBarStore();
  const navigate = useNavigate();

  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { currentSelectedFormulaId, setCurrentSelectedFormulaId } = useExposureStore();

  // Single exposure calculation for the whole app
  const { exposureByFormula, exposure: totalExposure } = useExposureCalculation({ useHorizon: true });

  // Build card data from account formulas + exposure
  const buttons: singleMaterialCardProps[] = React.useMemo(() => {
    if (!account?.formulasToAccount) return [];

    const emptyRiskTolerance = {
      dangerMinus: 0, warnMinus: 0, warnPlus: 0, dangerPlus: 0,
    };

    // General card
    const generalCard: singleMaterialCardProps[] = config.includeGeneral
      ? [{
          formulaId: GENERAL_ID,
          materialName: 'General',
          exposureStatus: getExposureStatus(
            emptyRiskTolerance,
            totalExposure?.netExposure?.valueBase ?? 0,
          ),
          cardColor: '#6b7280',
        }]
      : [];

    // Per-formula cards
    let formulas = account.formulasToAccount;

    // Filter watch-only
    if (config.filterWatchOnly) {
      formulas = formulas.filter(f => !f.isPriceWatchOnly);
    }

    // Filter to specific formula codes
    if (config.filterFormulaCodes) {
      const allowed = new Set(config.filterFormulaCodes);
      formulas = formulas.filter(f => allowed.has(f.formulaId));
    }

    const formulaCards: singleMaterialCardProps[] = formulas.map(({ formulaId, displayName, quoteSourceSymbol, isPriceWatchOnly, RiskTolerance }) => {
      const netValue = exposureByFormula?.[formulaId]?.netExposure?.valueBase ?? 0;

      return {
        formulaId,
        quoteSourceSymbol,
        materialName:
          displayName ||
          materialsMap[formulaId]?.name ||
          formulaId,
        exposureStatus: getExposureStatus(
          RiskTolerance || emptyRiskTolerance,
          netValue,
        ),
        cardColor: colorsMap[formulaId] || '',
        isPriceWatchOnly: config.filterWatchOnly ? undefined : isPriceWatchOnly,
        disabled: config.disabledFormulaCodes?.includes(formulaId),
      };
    });

    return [...generalCard, ...formulaCards];
  }, [account, materialsMap, colorsMap, exposureByFormula, totalExposure, config]);

  const handleSelect = React.useCallback((formulaId: string) => {
    setCurrentSelectedFormulaId(formulaId);
    if (config.onSelectPath) {
      const path = formulaId === GENERAL_ID
        ? config.onSelectPath
        : `${config.onSelectPath}/${formulaId}`;
      navigate(path);
    }
  }, [config.onSelectPath, navigate, setCurrentSelectedFormulaId]);

  if (!config.visible || buttons.length === 0) return null;

  return (
    <MaterialTrendButton
      buttons={buttons}
      currentMaterial={currentSelectedFormulaId}
      setCurrentMaterial={handleSelect}
      compact={config.compact}
    />
  );
};
